import { X } from "lucide-react";
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

// ─── shared ─────────────────────────────────────────────────────────────────

function useEscape(active: boolean, onEscape: () => void) {
  useEffect(() => {
    if (!active) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onEscape();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [active, onEscape]);
}

let scrollLocks = 0;
function useScrollLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    scrollLocks++;
    document.body.style.overflow = "hidden";
    return () => {
      scrollLocks--;
      if (scrollLocks <= 0) document.body.style.overflow = "";
    };
  }, [active]);
}

function Portal({ children }: { children: ReactNode }) {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}

// ─── Modal ──────────────────────────────────────────────────────────────────

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  className?: string;
  bodyClassName?: string;
  /** Hide the × button (e.g. blocking confirmation). */
  hideClose?: boolean;
  labelledBy?: string;
}

const modalSizes = { sm: "max-w-[420px]", md: "max-w-[560px]", lg: "max-w-[760px]", xl: "max-w-[1040px]", full: "max-w-[min(1280px,96vw)]" };

export function Modal({ open, onClose, title, description, children, footer, size = "md", className, bodyClassName, hideClose }: ModalProps) {
  useEscape(open, onClose);
  useScrollLock(open);
  if (!open) return null;
  return (
    <Portal>
      <div className="fixed inset-0 z-[80] flex items-end justify-center p-0 sm:items-center sm:p-6">
        <div className="absolute inset-0 animate-fade-in bg-black/65 backdrop-blur-[3px]" onClick={onClose} aria-hidden />
        <div
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === "string" ? title : undefined}
          className={cn(
            "relative flex max-h-[92vh] w-full animate-pop-in flex-col overflow-hidden rounded-t-2xl border border-line-strong bg-[#1a1a1c] shadow-pop sm:rounded-2xl",
            modalSizes[size],
            className,
          )}
        >
          {(title || !hideClose) && (
            <div className="flex items-start justify-between gap-4 px-5 pt-5">
              <div className="min-w-0">
                {title && <h2 className="text-[17px] font-semibold tracking-[-0.01em]">{title}</h2>}
                {description && <p className="mt-1 text-[13px] text-muted">{description}</p>}
              </div>
              {!hideClose && (
                <button type="button" onClick={onClose} aria-label="Close" className="-mr-1 -mt-1 grid size-8 place-items-center rounded-lg text-muted hover:bg-hover hover:text-fg">
                  <X className="size-4.5" />
                </button>
              )}
            </div>
          )}
          <div className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-4 scrollbar-thin", bodyClassName)}>{children}</div>
          {footer && <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3.5">{footer}</div>}
        </div>
      </div>
    </Portal>
  );
}

// ─── Drawer (right slide-over) ──────────────────────────────────────────────

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  headerExtra?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: "sm" | "md" | "lg" | "xl" | "full";
  className?: string;
}

const drawerWidths = { sm: "sm:w-[420px]", md: "sm:w-[540px]", lg: "sm:w-[720px]", xl: "sm:w-[920px]", full: "sm:w-[calc(100vw-var(--rail-w))]" };

export function Drawer({ open, onClose, title, subtitle, headerExtra, children, footer, width = "md", className }: DrawerProps) {
  useEscape(open, onClose);
  useScrollLock(open);
  if (!open) return null;
  return (
    <Portal>
      <div className="fixed inset-0 z-[70]">
        <div className="absolute inset-0 animate-fade-in bg-black/55" onClick={onClose} aria-hidden />
        <aside
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === "string" ? title : undefined}
          className={cn("absolute inset-y-0 right-0 flex w-full animate-slide-in-right flex-col border-l border-line bg-[#1c1c1e] shadow-pop", drawerWidths[width], className)}
        >
          <div className="shrink-0 border-b border-line/70 px-5 pb-3 pt-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {title && <h2 className="text-[17px] font-semibold tracking-[-0.01em]">{title}</h2>}
                {subtitle && <div className="mt-0.5 text-[12.5px] text-muted">{subtitle}</div>}
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="-mr-1 grid size-8 shrink-0 place-items-center rounded-lg text-muted hover:bg-hover hover:text-fg">
                <X className="size-4.5" />
              </button>
            </div>
            {headerExtra && <div className="mt-3">{headerExtra}</div>}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 scrollbar-thin">{children}</div>
          {footer && <div className="shrink-0 border-t border-line px-5 py-3">{footer}</div>}
        </aside>
      </div>
    </Portal>
  );
}

// ─── Popover (portal + fixed positioning; safe inside overflow containers) ─

export type Side = "bottom" | "top" | "right" | "left";
export type Align = "start" | "end" | "center";

export function usePopover() {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement | null>(null);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  const close = useCallback(() => setOpen(false), []);
  return { open, setOpen, toggle, close, anchorRef };
}

export interface PopoverPanelProps {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  side?: Side;
  align?: Align;
  offset?: number;
  className?: string;
  children: ReactNode;
  matchWidth?: boolean;
  /** Keep open while the pointer is over anchor or panel (hover flyouts). */
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  role?: string;
}

export function PopoverPanel({
  open,
  onClose,
  anchorRef,
  side = "bottom",
  align = "start",
  offset = 6,
  className,
  children,
  matchWidth,
  onPointerEnter,
  onPointerLeave,
  role = "menu",
}: PopoverPanelProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; minWidth?: number } | null>(null);

  const place = useCallback(() => {
    const a = anchorRef.current;
    const p = panelRef.current;
    if (!a || !p) return;
    const r = a.getBoundingClientRect();
    const pw = p.offsetWidth;
    const ph = p.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = 0;
    let left = 0;
    if (side === "bottom" || side === "top") {
      top = side === "bottom" ? r.bottom + offset : r.top - offset - ph;
      if (side === "bottom" && top + ph > vh - 8 && r.top - offset - ph > 8) top = r.top - offset - ph;
      if (side === "top" && top < 8) top = r.bottom + offset;
      left = align === "start" ? r.left : align === "end" ? r.right - pw : r.left + r.width / 2 - pw / 2;
    } else {
      left = side === "right" ? r.right + offset : r.left - offset - pw;
      top = align === "start" ? r.top : align === "end" ? r.bottom - ph : r.top + r.height / 2 - ph / 2;
    }
    left = Math.max(8, Math.min(left, vw - pw - 8));
    top = Math.max(8, Math.min(top, vh - ph - 8));
    setPos({ top, left, minWidth: matchWidth ? r.width : undefined });
  }, [anchorRef, side, align, offset, matchWidth]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, onClose, anchorRef]);
  useEscape(open, onClose);

  if (!open) return null;
  return (
    <Portal>
      <div
        ref={panelRef}
        role={role}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, minWidth: pos?.minWidth, visibility: pos ? "visible" : "hidden" }}
        className={cn("z-[90] animate-pop-in rounded-xl border border-line-strong bg-[#1d1d1f] p-1 shadow-pop", className)}
      >
        {children}
      </div>
    </Portal>
  );
}

/** Convenience dropdown: `trigger` is an element that gets ref + onClick injected. */
export function Dropdown({
  trigger,
  children,
  side = "bottom",
  align = "start",
  className,
  matchWidth,
}: {
  trigger: ReactElement<{ ref?: unknown; onClick?: (e: React.MouseEvent) => void; "aria-expanded"?: boolean }>;
  children: ReactNode | ((close: () => void) => ReactNode);
  side?: Side;
  align?: Align;
  className?: string;
  matchWidth?: boolean;
}) {
  const p = usePopover();
  const t = isValidElement(trigger)
    ? cloneElement(trigger, {
        ref: (el: HTMLElement | null) => {
          p.anchorRef.current = el;
        },
        onClick: (e: React.MouseEvent) => {
          trigger.props.onClick?.(e);
          p.toggle();
        },
        "aria-expanded": p.open,
      })
    : trigger;
  return (
    <>
      {t}
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} side={side} align={align} className={className} matchWidth={matchWidth}>
        {typeof children === "function" ? children(p.close) : children}
      </PopoverPanel>
    </>
  );
}

export function MenuItem({
  children,
  icon,
  onClick,
  danger,
  disabled,
  selected,
  description,
  right,
  className,
}: {
  children: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
  selected?: boolean;
  description?: ReactNode;
  right?: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors hover:bg-hover disabled:opacity-40 disabled:hover:bg-transparent",
        danger ? "text-danger" : "text-fg",
        selected && "bg-elevated",
        className,
      )}
    >
      {icon && <span className={cn("grid size-4 shrink-0 place-items-center", danger ? "text-danger" : "text-muted")}>{icon}</span>}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{children}</span>
        {description && <span className="mt-0.5 block text-[11.5px] leading-snug text-muted">{description}</span>}
      </span>
      {right}
    </button>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="px-2.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-subtle">{children}</div>;
}

export function MenuSeparator() {
  return <div className="my-1 h-px bg-line" />;
}

// ─── Tooltip (white pill, PlayAd rail style) ───────────────────────────────

export function Tooltip({
  content,
  children,
  side = "top",
  className,
  delay = 120,
  disabled,
}: {
  content: ReactNode;
  children: ReactNode;
  side?: Side;
  className?: string;
  delay?: number;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const show = () => {
    if (disabled) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), delay);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    setOpen(false);
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <span ref={ref} className="inline-flex" onPointerEnter={show} onPointerLeave={hide} onFocus={show} onBlur={hide}>
      {children}
      {open && content ? (
        <TooltipBubble anchor={ref} side={side} className={className}>
          {content}
        </TooltipBubble>
      ) : null}
    </span>
  );
}

function TooltipBubble({ anchor, side, children, className }: { anchor: RefObject<HTMLElement | null>; side: Side; children: ReactNode; className?: string }) {
  const el = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    const a = anchor.current?.getBoundingClientRect();
    const b = el.current;
    if (!a || !b) return;
    const w = b.offsetWidth;
    const h = b.offsetHeight;
    let top = 0;
    let left = 0;
    if (side === "right") {
      left = a.right + 10;
      top = a.top + a.height / 2 - h / 2;
    } else if (side === "left") {
      left = a.left - 10 - w;
      top = a.top + a.height / 2 - h / 2;
    } else if (side === "bottom") {
      top = a.bottom + 8;
      left = a.left + a.width / 2 - w / 2;
    } else {
      top = a.top - 8 - h;
      left = a.left + a.width / 2 - w / 2;
    }
    setPos({ top: Math.max(6, top), left: Math.max(6, Math.min(left, window.innerWidth - w - 6)) });
  }, [anchor, side]);
  return (
    <Portal>
      <div
        ref={el}
        role="tooltip"
        style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? "visible" : "hidden" }}
        className={cn(
          "pointer-events-none z-[100] max-w-xs animate-fade-in rounded-md bg-white px-2 py-1 text-[12px] font-medium leading-snug text-black shadow-lg",
          className,
        )}
      >
        {children}
      </div>
    </Portal>
  );
}
